import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useSession } from '../context/SessionContext';
import useSkillController from './useSkillController';
import useAchievementController from './useAchievementController';

export default function useEditorController() {
  const { session } = useSession();
  const { skills, fetchSkills } = useSkillController();
  const { achievements, fetchAchievements } = useAchievementController();
  const [portfolios, setPortfolios] = useState([]);
  const [profile, setProfile] = useState({
    avatarUrl: '',
    title_web: '',
    bio_web: '',
    fullname: '',
    email: '',
    phone: '',
    github: '',
    linkedin: '',
    behance: '',
    instagram: '',
    youtube: '',
    personal_web: '',
    show_avatar: false,
    show_email: false,
    show_phone: false,
    show_github: false,
    show_linkedin: false,
    show_behance: false,
    show_instagram: false,
    show_youtube: false,
    show_personal_web: false
  });
  const [experiences, setExperiences] = useState([]);
  const [educations, setEducations] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedItems, setSelectedItems] = useState({
    profile: false,
    experiences: [],
    educations: [],
    certificates: [],
    publications: [],
    skills: [],
    achievements: [],
    portfolios: []
  });

  const handleError = (error, message) => {
    console.error(message, error);
    setError(message);
    setLoading(false);
  };

  const fetchProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('profile')
        .select('*')
        .eq('user_id', session.user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      
      if (data) {
        let avatarUrl = '';
        const { data: imageData } = await supabase
          .from('image')
          .select('path')
          .eq('user_id', session.user.id)
          .eq('category', 'avatar')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (imageData) {
          const { data: { publicUrl } } = supabase
            .storage
            .from('document')
            .getPublicUrl(imageData.path);
          avatarUrl = publicUrl;
        }

        setProfile({
          ...data,
          avatarUrl
        });
      }
    } catch (error) {
      handleError(error, 'Failed to fetch profile');
    }
  };

  const updateProfile = async (updatedData) => {
    try {
      const { data, error } = await supabase
        .from('profile')
        .upsert({ ...updatedData, user_id: session.user.id })
        .select()
        .single();
  
      if (error) throw error;
      setProfile(data);
      return data;
    } catch (error) {
      handleError(error, 'Failed to update profile');
      return null;
    }
  };

  const fetchData = async (table, setState, imageCategory) => {
    try {
      const { data, error } = await supabase
        .from(table)
        .select('*')
        .eq('user_id', session.user.id);

      if (error) throw error;

      if (imageCategory) {
        const dataWithImages = await Promise.all(
          data.map(async item => {
            const { data: images } = await supabase
              .from('image')
              .select('path')
              .eq('category_id', item.id)
              .eq('category', imageCategory);

            return {
              ...item,
              images: images?.map(img => ({
                publicUrl: supabase.storage.from('document').getPublicUrl(img.path).data.publicUrl
              })) || []
            };
          })
        );
        setState(dataWithImages);
      } else {
        setState(data);
      }
    } catch (error) {
      handleError(error, `Failed to fetch ${table} data`);
    }
  };

  const createItem = async (table, data, imageCategory, images) => {
    try {
      const { data: newItem, error } = await supabase
        .from(table)
        .insert([{ ...data, user_id: session.user.id }])
        .select()
        .single();

      if (error) throw error;

      if (images?.length > 0) {
        await Promise.all(images.map(async (image) => {
          const fileExt = image.name.split('.').pop();
          const fileName = `${Math.random()}.${fileExt}`;
          const filePath = `${fileName}`;

          await supabase.storage
            .from('document')
            .upload(filePath, image);

          await supabase
            .from('image')
            .insert({
              user_id: session.user.id,
              category: imageCategory,
              category_id: newItem.id,
              path: filePath
            });
        }));
      }

      return newItem;
    } catch (error) {
      handleError(error, `Failed to create ${table}`);
      return null;
    }
  };

  const fetchExperiences = () => fetchData('experience', setExperiences, 'experience');
  const addExperience = (data, images) => createItem('experience', data, 'experience', images);

  const fetchEducations = () => fetchData('education', setEducations, 'education');
  const addEducation = (data, images) => createItem('education', data, 'education', images);

  const fetchPortfolios = () => fetchData('portofolio', setPortfolios, 'portofolio');
  const addPortfolio = (data, images) => createItem('portofolio', data, 'portofolio', images);

  const saveCV = async (cvData, template) => {
    try {
      const { data, error } = await supabase
        .from('cv')
        .upsert({
          user_id: session.user.id,
          cv_data: {
            ...cvData,
            profile: cvData.profile ? {
              ...cvData.profile,
              avatarUrl: profile.avatarUrl
            } : null
          },
          template: template
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      handleError(error, 'Failed to save CV');
      return null;
    }
  };

  const fetchCV = async (id) => {
    try {
      const { data, error } = await supabase
        .from('cv')
        .select('*')
        .eq('user_id', session.user.id)
        .eq('id', id)
        .maybeSingle();
  
      if (error) throw error;
      return data;
    } catch (error) {
      handleError(error, 'Failed to fetch CV');
      return null;
    }
  };

  const updateCV = async (cvId, cvData, template) => {
    try {
      const { data, error } = await supabase
        .from('cv')
        .update({
          cv_data: {
            ...cvData,
            profile: cvData.profile ? {
              ...cvData.profile,
              avatarUrl: profile.avatarUrl
            } : null
          },
          template: template
        })
        .eq('id', cvId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      handleError(error, 'Failed to update CV');
      return null;
    }
  };

  useEffect(() => {
    if (session) {
      const fetchAll = async () => {
        setLoading(true);
        try {
          await Promise.all([
            fetchProfile(),
            fetchExperiences(),
            fetchEducations(),
            fetchData('certificate', setCertificates),
            fetchData('publication', setPublications),
            fetchSkills(),
            fetchAchievements(),
            fetchPortfolios()
          ]);
        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchAll();
    }
  }, [session]);

  const toggleItem = (section, id) => {
    setSelectedItems(prev => ({
      ...prev,
      [section]: prev[section]?.includes(id) 
        ? prev[section].filter(itemId => itemId !== id)
        : [...prev[section], id]
    }));
  };

  const toggleProfile = () => {
    setSelectedItems(prev => ({
      ...prev,
      profile: !prev.profile
    }));
  };

  return {
    profile,
    experiences,
    educations,
    certificates,
    publications,
    skills,
    achievements,
    portfolios,
    loading,
    error,
    selectedItems,
    updateProfile,
    addExperience,
    addEducation,
    addPortfolio,
    saveCV,
    fetchCV,
    updateCV,
    toggleItem,
    toggleProfile,
    setSelectedItems
  };
}